import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { convertBoolTypeForMixPanel } from '../../../../../tracking-service/tracking-values';
import { isNoContractRequiredRecordType, STANDARD_ORDER_RECORD_TYPE, toAllowedRecordTypeName } from './order-field-visibility';
import { SalesforceSharedFieldsTrackingMapper } from './salesforce-shared-fields-tracking.mapper';

interface ICreatedOrderRef {
    orderId?: string | null;
    orderNumber?: string | null;
}

interface IAddSalesforceOrderTrackingOptions {
    productCount?: number;
    order?: ICreatedOrderRef;
    billingConfirmation?: boolean | null;
    recordType?: string;
    includeBusinessLead?: boolean;
    businessLeadName?: string;
    businessLeadChanged?: boolean;
}

const yesNo = (value: boolean | null | undefined): string => {
    if (value === true) {
        return 'Yes';
    }
    if (value === false) {
        return 'No';
    }
    return '';
};

export class AddSalesforceOrderTrackingMapper {
    public static toTrackingEvent(
        event: IEventDetailsDTO,
        values: Record<string, string>,
        options: IAddSalesforceOrderTrackingOptions = {},
    ): Record<string, string | boolean> {
        const {
            productCount = 0,
            order = {},
            billingConfirmation = null,
            recordType = STANDARD_ORDER_RECORD_TYPE,
            includeBusinessLead = false,
            businessLeadName = '',
            businessLeadChanged = false,
        } = options;
        return {
            ...SalesforceSharedFieldsTrackingMapper.toTrackingProperties(event),
            'Account Name': event?.accountName ?? '',
            'Account ID': event?.accountId != null ? String(event.accountId) : '',
            'Event ID': event?.eventId != null ? String(event.eventId) : '',
            'Order ID': order.orderId ?? '',
            'Order Number': order.orderNumber ?? '',
            'Contract #': isNoContractRequiredRecordType(recordType) ? '' : (values.contractNumber ?? ''),
            'Multi Event Order': values.multiEventOrder ?? '',
            'Parent Order': values.parentOrder ?? '',
            'Corporate Related': values.corporateRelated ?? '',
            'Opportunity': values.opportunity ?? '',
            'Channel/Agency Referral': values.channelAgencyReferral ?? '',
            'Host LOB': values.hostLob ?? '',
            'Host Sub LOB': values.hostSubLob ?? '',
            'Order Type': values.orderType ?? '',
            'Order Subtype': values.orderSubtype ?? '',
            'Event Type (OE Defined)': values.eventType ?? '',
            'Event Type (Client Defined)': values.eventTypeClient ?? '',
            'Sync to Teams': convertBoolTypeForMixPanel(values.syncToTeams === 'Yes'),
            'No Staffing Required': convertBoolTypeForMixPanel(values.noStaffRequired === 'Yes'),
            'Hybrid Event': values.hybridEvent ?? '',
            'High Profile Order': convertBoolTypeForMixPanel(values.highProfileOrder === 'Yes'),
            'High Profile Reasoning': values.highProfileReasoning ?? '',
            'High Profile Attendee Details Provided': convertBoolTypeForMixPanel(Boolean(values.highProfileAttendeeDetails)),
            'Account Legal Entity': values.accountLegalEntity ?? '',
            'Is This Event Repeatable': values.isThisEventRepeatable ?? '',
            'Lasso Sync Status': values.lassoSyncStatus ?? '',
            'Lasso Sync Datetime': values.lassoSyncDatetime ?? '',
            'Teams Created': values.teamsCreated ?? '',
            'Teams Channel Archived': convertBoolTypeForMixPanel(values.processedForArchive === 'Yes'),
            'Billing Time Sensitivity': values.billingTimeSensitivity ?? '',
            'Price Book': values.priceBook ?? '',
            'Billing Method': values.billingMethod ?? '',
            'Billing Contact': values.billingContact ?? '',
            'Billing Details Confirmation': yesNo(billingConfirmation),
            'Event Ran Smoothly': values.eventRanSmoothly ?? '',
            'Q&A Chat Enabled': values.qaChatEnabled ?? '',
            'Q&A Chat Sent': values.qaReportSent ?? '',
            'Product Count': String(productCount),
            'Currency': values.currency ?? '',
            'Record Type': toAllowedRecordTypeName(recordType) ?? STANDARD_ORDER_RECORD_TYPE,
            ...(includeBusinessLead
                ? {
                    'Business Lead': businessLeadName,
                    'Business Lead Changed': businessLeadChanged,
                }
                : {}),
        };
    }

    public static toLinkTrackingEvent(
        event: IEventDetailsDTO,
        options: ICreatedOrderRef & { productCount?: number } = {},
    ): Record<string, string> {
        return {
            ...SalesforceSharedFieldsTrackingMapper.toTrackingProperties(event),
            'Account Name': event?.accountName ?? '',
            'Account ID': event?.accountId != null ? String(event.accountId) : '',
            'Event ID': event?.eventId != null ? String(event.eventId) : '',
            'Order ID': options.orderId ?? '',
            'Order Number': options.orderNumber ?? '',
            'Product Count': String(options.productCount ?? 0),
        };
    }
}
